// The Director's on-screen presence: a small film light, drawn in code (SVG + CSS, no images). Six characters
// (src/directors.ts), each a different light. No face: the lens (or the paper, the tube, the panel, the filament)
// is the face, and it lives by how it glows:
//   idle       a pilot glow that breathes very slowly
//   warming    connecting: the lamp comes up
//   listening  a little dimmer, breathing gently while the user talks
//   working    steady and warm; a quick warm flicker each time it starts making something (flickerDirector)
//   speaking   bright, and the glow pulses with the Director's real output level (director.levels())
//   error      cold and dark
// Cost: React renders only when the status or preset changes. While speaking, one rAF loop per light reads the
// output level and writes it to a CSS variable (--lv) on the light; everything else is CSS. prefers-reduced-motion:
// no breathing, no flicker, no loop (a fixed bright level while speaking).
import { useEffect, useId, useRef } from "react";
import { director } from "../director";
import { onDirectorWork, presetOf, type PresetId } from "../directors";
import type { DirectorStatus } from "../store";

export type LightState = "idle" | "warming" | "listening" | "working" | "speaking" | "error";

export const LIGHT_STATE: Record<DirectorStatus, LightState> = {
  off: "idle",
  connecting: "warming",
  listening: "listening",
  thinking: "working",
  speaking: "speaking",
  error: "error",
};

/** Per character: where its glow sits (viewBox units) and its two light colours (hot core, edge). */
const GLOW: Record<PresetId, { cx: number; cy: number; r: number; hot: string; edge: string; halo: string }> = {
  keylight: { cx: 0, cy: 0, r: 58, hot: "#FFF3DA", edge: "#F39A1E", halo: "#FFB23E" },
  lantern: { cx: 0, cy: 6, r: 60, hot: "#FFF6E6", edge: "#F7B45A", halo: "#FFC874" },
  tube: { cx: 0, cy: 0, r: 56, hot: "#F2FDFF", edge: "#2FC3DB", halo: "#50D2E6" },
  daylight: { cx: 0, cy: 2, r: 60, hot: "#F4F8FF", edge: "#6F98F5", halo: "#84AAFF" },
  practical: { cx: 0, cy: -9, r: 52, hot: "#FFF1D2", edge: "#F39A1E", halo: "#FFB23E" },
  spot: { cx: 30, cy: -10, r: 46, hot: "#FFF6E4", edge: "#F7A93A", halo: "#FFC874" },
};

export function DirectorLight({
  preset,
  status,
  size = 56,
  className = "",
  title,
}: {
  preset: PresetId;
  status: DirectorStatus | LightState;
  size?: number | string;
  className?: string;
  /** a label for screen readers; without it the light is decorative */
  title?: string;
}) {
  const root = useRef<HTMLSpanElement>(null);
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const state: LightState = (LIGHT_STATE as Record<string, LightState>)[status] ?? (status as LightState);
  const p = presetOf(preset);
  const g = GLOW[p.id];
  const small = typeof size === "number" && size < 40;

  // speaking: the glow follows the Director's real output level, eased so it pulses rather than buzzes
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (state !== "speaking" || reduce) {
      el.style.setProperty("--lv", state === "speaking" ? "0.55" : "0");
      return;
    }
    let raf = 0;
    let lv = 0.3;
    let shown = -1;
    let last = performance.now();
    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      let out = 0;
      try {
        out = director.levels().output;
      } catch {
        /* no live link yet */
      }
      // the SDK's level sits mostly under 0.6: lift it, and rise faster than it falls (a lamp's filament)
      const target = Math.min(1, Math.max(0, out) * 1.6);
      const k = 1 - Math.exp(-dt * (target > lv ? 22 : 7));
      lv += (target - lv) * k;
      if (Math.abs(lv - shown) > 0.006) {
        shown = lv;
        el.style.setProperty("--lv", lv.toFixed(3));
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [state]);

  // working: a quick warm flicker whenever the Director starts making something (Web Animations: no reflow)
  useEffect(() => {
    const el = root.current;
    if (!el || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    return onDirectorWork(() => {
      // deltas added to each part's own state opacity (composite "add"), so a dim light dips from dim and a dark
      // one stays dark
      const opts = { duration: 620, easing: "linear", composite: "add" } as KeyframeAnimationOptions;
      const lamp = [{ opacity: 0 }, { opacity: -0.5, offset: 0.12 }, { opacity: 0.1, offset: 0.24 }, { opacity: -0.28, offset: 0.4 }, { opacity: 0.06, offset: 0.55 }, { opacity: 0 }];
      el.querySelectorAll<SVGElement>(".dl-lit").forEach((x) => x.animate?.(lamp, opts));
      el.querySelector<SVGElement>(".dl-halo-flick")?.animate?.([{ opacity: 0 }, { opacity: -0.6, offset: 0.12 }, { opacity: 0, offset: 0.26 }, { opacity: -0.35, offset: 0.42 }, { opacity: 0 }], opts);
    });
  }, []);

  const id = (k: string) => `dl-${k}-${uid}`;
  return (
    <span
      ref={root}
      className={`dl dl-${p.id} dl-${state}${small ? " is-small" : ""} ${className}`}
      style={{ width: size, height: size }}
      {...(title ? { role: "img", "aria-label": title } : { "aria-hidden": true })}
    >
      <svg viewBox="-60 -60 120 120" width="100%" height="100%" focusable="false" aria-hidden>
        <defs>
          <radialGradient id={id("halo")} cx={g.cx} cy={g.cy} r={g.r} gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor={g.halo} stopOpacity="0.62" />
            <stop offset="0.45" stopColor={g.halo} stopOpacity="0.2" />
            <stop offset="1" stopColor={g.halo} stopOpacity="0" />
          </radialGradient>
          <radialGradient id={id("lit")} cx="0.42" cy="0.38" r="0.7">
            <stop offset="0" stopColor={g.hot} />
            <stop offset="0.38" stopColor={g.halo} />
            <stop offset="1" stopColor={g.edge} />
          </radialGradient>
          <linearGradient id={id("body")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--dl-body-a)" />
            <stop offset="1" stopColor="var(--dl-body-b)" />
          </linearGradient>
          <linearGradient id={id("door")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--dl-door-a)" />
            <stop offset="1" stopColor="var(--dl-door-b)" />
          </linearGradient>
          <linearGradient id={id("beam")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={g.halo} stopOpacity="0.55" />
            <stop offset="1" stopColor={g.halo} stopOpacity="0" />
          </linearGradient>
        </defs>
        <g className="dl-halo-flick">
          <circle className="dl-halo" cx={g.cx} cy={g.cy} r={g.r} fill={`url(#${id("halo")})`} />
        </g>
        <Body id={p.id} lit={`url(#${id("lit")})`} body={`url(#${id("body")})`} door={`url(#${id("door")})`} beam={`url(#${id("beam")})`} />
      </svg>
    </span>
  );
}

type Paint = { lit: string; body: string; door: string; beam: string };

/** Each light's drawing. `.dl-off` is the unlit glass, `.dl-lit` the glow over it (its opacity is the state). */
function Body({ id, lit, body, door, beam }: Paint & { id: PresetId }) {
  switch (id) {
    case "keylight":
      // a tungsten Fresnel: barn doors opened out, a rounded housing, a stepped Fresnel lens
      return (
        <g>
          <path className="dl-door" fill={door} d="M-31,-23 L-49,-35 Q-55,-38.5 -55,-32 L-55,32 Q-55,38.5 -49,35 L-31,23 Z" />
          <path className="dl-door" fill={door} d="M31,-23 L49,-35 Q55,-38.5 55,-32 L55,32 Q55,38.5 49,35 L31,23 Z" />
          <rect className="dl-housing" fill={body} x={-33} y={-33} width={66} height={66} rx={18} />
          <rect className="dl-rim-hi" x={-31.5} y={-31.5} width={63} height={63} rx={16.5} />
          <circle className="dl-off" r={23} />
          <g className="dl-lit">
            <circle r={23} fill={lit} />
            <circle className="dl-ring dl-fine" r={16.5} />
            <circle className="dl-ring" r={10} />
          </g>
          <circle className="dl-bezel" r={23} />
          <ellipse className="dl-glint" cx={-8} cy={-11} rx={7} ry={3.6} transform="rotate(-35 -8 -11)" />
        </g>
      );
    case "lantern":
      // a china-ball lantern: paper on wire ribs, hanging from its cord
      return (
        <g>
          <path className="dl-cord" d="M0,-60 V-40" />
          <rect className="dl-housing" fill={body} x={-9} y={-42} width={18} height={9} rx={2.6} />
          <circle className="dl-off" cy={6} r={41} />
          <g className="dl-lit">
            <circle cy={6} r={41} fill={lit} />
          </g>
          <g className="dl-ribs">
            <path d="M-29,-20 Q0,-13 29,-20" />
            <path d="M-39,-4 Q0,4 39,-4" />
            <path d="M-39,15 Q0,23 39,15" />
            <path d="M-28,32 Q0,39 28,32" />
          </g>
          <circle className="dl-bezel dl-bezel-soft" cy={6} r={41} />
          <ellipse className="dl-glint" cx={-15} cy={-14} rx={9} ry={4.2} transform="rotate(-30 -15 -14)" />
        </g>
      );
    case "tube":
      // an LED tube, leaning a little: two end caps, the diffuser between, a bright strip down the middle
      return (
        <g transform="rotate(14)">
          <rect className="dl-housing" fill={body} x={-14} y={-52} width={28} height={104} rx={11} />
          <rect className="dl-off" x={-11} y={-38} width={22} height={76} rx={6} />
          <g className="dl-lit">
            <rect x={-11} y={-38} width={22} height={76} rx={6} fill={lit} />
            <rect className="dl-strip" x={-2.2} y={-33} width={4.4} height={66} rx={2.2} />
          </g>
          <path className="dl-cap-line" d="M-14,-38 H14 M-14,38 H14" />
          <rect className="dl-glint" x={-8.5} y={-34} width={3} height={30} rx={1.5} />
        </g>
      );
    case "daylight":
      // an HMI daylight panel: a top flag, the housing, a diffusion panel with its grid, a small yoke
      return (
        <g>
          <path className="dl-door" fill={door} d="M-40,-29 L-45,-43 Q-46.5,-47 -42,-47 L42,-47 Q46.5,-47 45,-43 L40,-29 Z" />
          <path className="dl-yoke" d="M-26,36 V45 H26 V36 M0,45 V54" />
          <rect className="dl-housing" fill={body} x={-47} y={-31} width={94} height={70} rx={12} />
          <rect className="dl-rim-hi" x={-45.5} y={-29.5} width={91} height={67} rx={10.5} />
          <rect className="dl-off" x={-38} y={-22} width={76} height={52} rx={6} />
          <g className="dl-lit">
            <rect x={-38} y={-22} width={76} height={52} rx={6} fill={lit} />
          </g>
          <path className="dl-grid" d="M-12.7,-22 V30 M12.7,-22 V30 M-38,4 H38" />
          <rect className="dl-bezel" x={-38} y={-22} width={76} height={52} rx={6} />
        </g>
      );
    case "practical":
      // a practical: a clear bulb with a looped filament, on a screw base
      return (
        <g>
          <path className="dl-off dl-glass" d="M-14,22 C-14,13 -31,5 -31,-11 A31,31 0 1 1 31,-11 C31,5 14,13 14,22 Z" />
          <g className="dl-lit">
            <path d="M-14,22 C-14,13 -31,5 -31,-11 A31,31 0 1 1 31,-11 C31,5 14,13 14,22 Z" fill={lit} className="dl-glass-lit" />
          </g>
          <path className="dl-filament-off" d="M-6,20 V2 C-6,-14 6,-14 6,2 V20" />
          <path className="dl-filament dl-lit" d="M-6,20 V2 C-6,-14 6,-14 6,2 V20" />
          <path className="dl-bezel dl-bezel-soft" d="M-14,22 C-14,13 -31,5 -31,-11 A31,31 0 1 1 31,-11 C31,5 14,13 14,22 Z" />
          <rect className="dl-housing" fill={body} x={-15} y={21} width={30} height={22} rx={3} />
          <path className="dl-threads" d="M-15,28 H15 M-15,35 H15" />
          <path className="dl-housing" fill={body} d="M-9,43 H9 L6,50 H-6 Z" />
          <ellipse className="dl-glint" cx={-14} cy={-22} rx={7} ry={3.6} transform="rotate(-48 -14 -22)" />
        </g>
      );
    case "spot":
      // a follow-spot on its stand: a long barrel tilted up, the front lens lit, its beam reaching out
      return (
        <g>
          <path className="dl-yoke" d="M-4,4 L-10,44 M-4,4 L4,44 M-22,46 H18" />
          <g transform="rotate(-18)">
            <path className="dl-beam" fill={beam} d="M36,-17 L76,-38 L76,38 L36,17 Z" />
            <rect className="dl-door" fill={door} x={-52} y={-12} width={12} height={24} rx={4} />
            <rect className="dl-housing" fill={body} x={-44} y={-16} width={66} height={32} rx={8} />
            <path className="dl-fins" d="M-30,-16 V16 M-21,-16 V16 M-12,-16 V16" />
            <rect className="dl-door" fill={door} x={20} y={-22} width={14} height={44} rx={5} />
            <ellipse className="dl-off" cx={34} cy={0} rx={7.5} ry={19} />
            <g className="dl-lit">
              <ellipse cx={34} cy={0} rx={7.5} ry={19} fill={lit} />
            </g>
            <ellipse className="dl-bezel" cx={34} cy={0} rx={7.5} ry={19} />
          </g>
        </g>
      );
  }
}
